import type { Total } from '@/js/types/cart.ts';
import type { DadataParams } from '@/js/types/dadata.ts';
import type { Option } from '@/js/types/options.ts';
import type { DeliveryType, OrderRequest, PaymentMethod } from '@/js/types/order.ts';
import { clearPhone } from '@/js/helper/clear-phone.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';

export const CALL_TIMES = [
  getLocaleValue('within_10_minutes'),
  getLocaleValue('within_20_minutes'),
  getLocaleValue('within_30_minutes'),
];

export const MOSCOW_REGION = 'Москва';
export const MOSKOVSKAYA_REGION = 'Московская';

export function getDadataAddressParams(): DadataParams {
  return {
    locations: [
      { region: MOSCOW_REGION },
      { region: MOSKOVSKAYA_REGION },
    ],
    from_bound: { value: 'street' },
    to_bound: { value: 'house' },
  };
}

export const PRODUCT_WORDS = ['товар', 'товара', 'товаров'] as const;

const TOTAL_NAME: Partial<Record<Total['type'], string>> = {
  sum: getLocaleValue('cost_of_goods'),
  sum_old: getLocaleValue('cost_of_goods_without_discount'),
};

export function getTotalName(type: Total['type']) {
  return TOTAL_NAME[type] ?? '';
}

type CheckoutDeliveryType = Extract<DeliveryType, 'pickup' | 'tk_terminal' | 'self'>;

export function getObtainingMethods(): { value: CheckoutDeliveryType, label: string }[] {
  return [
    { value: 'pickup', label: getLocaleValue('delivery_pickup') },
    { value: 'tk_terminal', label: getLocaleValue('delivery_tk_terminal') },
    { value: 'self', label: getLocaleValue('delivery_courier') },
  ];
}

export function getPaymentMethods(): { value: PaymentMethod, label: string }[] {
  return [
    // {
    //   value: 'sber',
    //   label: 'Сбербанк',
    // },
    {
      value: 'tbank_card',
      label: getLocaleValue('tbank_card_label'),
    },
    // {
    //   value: 'tbank_credit',
    //   label: 'Тбанк кредит',
    // },
    {
      value: 'transfer',
      label: getLocaleValue('transfer_label'),
    },
    {
      value: 'yandex_split',
      label: getLocaleValue('yandex_split_label'),
    },
  ];
}

export function getCallTimes(): Option[] {
  return CALL_TIMES.map(item => ({
    label: item,
    value: item,
  }));
}

export function getCourierDeliveryValidationError(
  address: string,
  isInsideDelivery: boolean,
  isOutsideDelivery: boolean,
): string | null {
  if (!address.trim()) {
    return getLocaleValue('enter_your_shipping_address');
  }
  if (!isInsideDelivery && !isOutsideDelivery) {
    return getLocaleValue('delivery_address_out_of_zone');
  }
  return null;
}

export function cleanOrderFields(data: OrderRequest) {
  let order: OrderRequest = {
    name: data.name,
    email: data.email,
    phone: clearPhone(data.phone),
    legal_type: data.legal_type,
    delivery_type: data.delivery_type,
    payment_method: data.payment_method,
  };

  if (data.delivery_data) {
    order = {
      ...order,
      delivery_data: data.delivery_data,
    };
  }

  if (data.legal_type === 'organization') {
    order = {
      ...order,
      legal_data: data.legal_data,
    };
  }

  if (data.lift_data && data.lift_data.lift_level && data.lift_data.lift_price) {
    order = {
      ...order,
      lift_data: data.lift_data,
    };
  }

  if (data.payment_data) {
    if (data.payment_data.bonus !== null) {
      order = {
        ...order,
        payment_data: {
          bonus: data.payment_data.bonus,
        },
      };
    }
    if (data.payment_data.coupon !== null) {
      order = {
        ...order,
        payment_data: {
          ...order.payment_data,
          coupon: data.payment_data.coupon,
        },
      };
    }
  }

  const { call_time, is_subscribed } = data.additional_data ?? {};

  const additional_data = {
    ...(call_time !== '' && { call_time }),
    ...(is_subscribed !== undefined && { is_subscribed }),
  };

  if (Object.keys(additional_data).length) {
    order = {
      ...order,
      additional_data,
    };
  }

  if (data.services && data.services.installment_items.length > 0) {
    order = {
      ...order,
      services: data.services,
    };
  }

  return order;
}
